<template>
  <div class="detail-extra">
    <nav class="detail-extra__tabs" aria-label="详情导航">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        :class="[
          'detail-extra__tab',
          { 'is-active': activeTab === tab.id }
        ]"
        @click="scrollToSection(tab.id)"
      >{{ tab.label }}</button>
    </nav>

    <section
      :id="sectionId('info')"
      ref="sectionInfo"
      class="detail-extra__section"
    >
      <h2 class="detail-extra__title">商标信息</h2>
      <table class="detail-extra__table">
        <tbody>
          <tr v-for="row in infoRows" :key="row.label">
            <th scope="row">{{ row.label }}</th>
            <td>{{ row.value }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section
      :id="sectionId('guarantee')"
      ref="sectionGuarantee"
      class="detail-extra__section"
    >
      <h2 class="detail-extra__title">交易保障</h2>
      <ul class="detail-extra__guarantee">
        <li
          v-for="item in guaranteeItems"
          :key="item.title"
          class="detail-extra__guarantee-item"
        >
          <img :src="item.icon" :alt="item.title" class="detail-extra__guarantee-icon" />
          <div class="detail-extra__guarantee-text">
            <p class="detail-extra__guarantee-title">{{ item.title }}</p>
            <p class="detail-extra__guarantee-desc">{{ item.desc }}</p>
          </div>
        </li>
      </ul>
    </section>

    <section
      :id="sectionId('transfer')"
      ref="sectionTransfer"
      class="detail-extra__section"
    >
      <h2 class="detail-extra__title">过户资料</h2>
      <div class="detail-extra__transfer">
        <div class="detail-extra__transfer-block">
          <h3 class="detail-extra__transfer-heading">买方提供资料</h3>
          <img
            :src="transferBuyerImage"
            alt="个人身份证与公司或个体营业执照"
            class="detail-extra__transfer-buyer"
          />
        </div>
        <div class="detail-extra__transfer-block">
          <h3 class="detail-extra__transfer-heading">购买后获得证书</h3>
          <ul class="detail-extra__transfer-certs">
            <li
              v-for="cert in transferCerts"
              :key="cert.label"
              class="detail-extra__transfer-cert"
            >
              <img :src="cert.icon" :alt="cert.label" class="detail-extra__transfer-cert-img" />
              <p class="detail-extra__transfer-cert-label">{{ cert.label }}</p>
              <p v-if="cert.note" class="detail-extra__transfer-cert-note">{{ cert.note }}</p>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section
      :id="sectionId('process')"
      ref="sectionProcess"
      class="detail-extra__section"
    >
      <h2 class="detail-extra__title">商标流程</h2>
      <ol class="detail-extra__process">
        <li
          v-for="(step, index) in processSteps"
          :key="step.title"
          class="detail-extra__process-item"
        >
          <img :src="step.icon" :alt="step.title" class="detail-extra__process-icon" />
          <p class="detail-extra__process-title">{{ step.title }}</p>
          <p class="detail-extra__process-desc">{{ step.desc }}</p>
          <span class="detail-extra__process-index" aria-hidden="true">
            -{{ formatStepIndex(index) }}-
          </span>
        </li>
      </ol>
    </section>
  </div>
</template>

<script>
import {
  DETAIL_EXTRA_TABS,
  DETAIL_GUARANTEE_ITEMS,
  DETAIL_PROCESS_STEPS,
  DETAIL_TRANSFER_BUYER_IMAGE,
  DETAIL_TRANSFER_CERTS
} from '@/constants/detailPage'

const SECTION_REF_MAP = {
  info: 'sectionInfo',
  guarantee: 'sectionGuarantee',
  transfer: 'sectionTransfer',
  process: 'sectionProcess'
}

export default {
  name: 'DetailExtraPanel',
  props: {
    detail: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      tabs: DETAIL_EXTRA_TABS,
      guaranteeItems: DETAIL_GUARANTEE_ITEMS,
      transferCerts: DETAIL_TRANSFER_CERTS,
      transferBuyerImage: DETAIL_TRANSFER_BUYER_IMAGE,
      processSteps: DETAIL_PROCESS_STEPS,
      activeTab: DETAIL_EXTRA_TABS[0].id
    }
  },
  computed: {
    infoRows() {
      const detail = this.detail || {}
      const validity = this.formatValidity(detail)
      const regDate = detail.valid_date_begin || '—'

      return [
        { label: '商标状态', value: validity !== '—' ? '已注册' : '—' },
        { label: '初审公告期号', value: '—' },
        { label: '初审公告日期', value: '—' },
        { label: '专用权期限', value: validity },
        { label: '注册公告期号', value: '—' },
        { label: '注册公告日期', value: regDate }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    sectionId(tabId) {
      return `detail-md-${tabId}`
    },
    scrollToSection(tabId) {
      const refName = SECTION_REF_MAP[tabId]
      const el = refName ? this.$refs[refName] : null
      if (!el) return

      this.activeTab = tabId
      const top = el.getBoundingClientRect().top + window.pageYOffset - 120
      window.scrollTo({ top, behavior: 'smooth' })
    },
    onScroll() {
      const offsets = this.tabs.map(tab => {
        const refName = SECTION_REF_MAP[tab.id]
        const el = refName ? this.$refs[refName] : null
        if (!el) return { id: tab.id, top: Infinity }
        return {
          id: tab.id,
          top: el.getBoundingClientRect().top
        }
      })

      const visible = offsets
        .filter(item => item.top <= 160)
        .sort((a, b) => b.top - a.top)

      if (visible.length) {
        this.activeTab = visible[0].id
      }
    },
    formatValidity(detail) {
      const begin = detail.valid_date_begin
      const end = detail.valid_date
      if (begin && end) return `${begin} 至 ${end}`
      if (end) return end
      return '—'
    },
    formatStepIndex(index) {
      return String(index + 1).padStart(2, '0')
    }
  }
}
</script>

<style scoped>
.detail-extra {
  background: var(--color-bg-page);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  overflow: hidden;
}

.detail-extra__tabs {
  display: flex;
  align-items: stretch;
  height: var(--detail-tab-height);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg-page);
  position: sticky;
  top: 0;
  z-index: 2;
}

.detail-extra__tab {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}

.detail-extra__tab:hover,
.detail-extra__tab.is-active {
  color: var(--color-primary);
}

.detail-extra__tab.is-active::after {
  content: '';
  position: absolute;
  left: 20%;
  right: 20%;
  bottom: 0;
  height: 2px;
  background: var(--color-primary);
}

.detail-extra__section {
  padding: var(--space-lg);
  border-bottom: 1px solid var(--color-border-light);
}

.detail-extra__section:last-child {
  border-bottom: none;
}

.detail-extra__title {
  font-size: var(--detail-section-title-size);
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--space-md);
}

.detail-extra__table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.detail-extra__table th,
.detail-extra__table td {
  border: 1px solid var(--color-border);
  padding: var(--space-sm) var(--space-md);
  text-align: left;
}

.detail-extra__table th {
  width: 28%;
  background: var(--color-bg-section);
  color: var(--color-text-muted);
  font-weight: 500;
}

.detail-extra__table td {
  color: var(--color-text-primary);
}

.detail-extra__guarantee {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-lg);
  list-style: none;
  padding: 0;
  margin: 0;
}

.detail-extra__guarantee-item {
  display: flex;
  gap: var(--space-md);
  align-items: flex-start;
}

.detail-extra__guarantee-icon {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  object-fit: contain;
}

.detail-extra__guarantee-title {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--space-xs);
}

.detail-extra__guarantee-desc {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  line-height: 1.7;
}

.detail-extra__transfer {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-md);
}

.detail-extra__transfer-block {
  background: var(--detail-transfer-bg);
  border-radius: var(--radius-md);
  padding: var(--space-md);
}

.detail-extra__transfer-heading {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--space-md);
  text-align: center;
}

.detail-extra__transfer-buyer {
  display: block;
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  object-fit: contain;
}

.detail-extra__transfer-certs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-md);
  list-style: none;
  padding: 0;
  margin: 0;
}

.detail-extra__transfer-cert {
  text-align: center;
}

.detail-extra__transfer-cert-img {
  width: 100%;
  max-width: 120px;
  margin: 0 auto;
  display: block;
  object-fit: contain;
}

.detail-extra__transfer-cert-label,
.detail-extra__transfer-cert-note {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.detail-extra__process {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-md);
  list-style: none;
  padding: 0;
  margin: 0;
}

.detail-extra__process-item {
  text-align: center;
  position: relative;
}

.detail-extra__process-icon {
  width: 72px;
  height: 72px;
  object-fit: contain;
  margin: 0 auto var(--space-sm);
  display: block;
}

.detail-extra__process-title {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--space-xs);
}

.detail-extra__process-desc {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  line-height: 1.7;
  min-height: 54px;
}

.detail-extra__process-index {
  display: inline-block;
  margin-top: var(--space-sm);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}
</style>
